/* eslint-disable max-lines */
import {action, computed, makeObservable, observable} from 'mobx';
import {getLinkPath, newsArticles, promotionArticles} from 'app/utils';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import PaginateInterface from 'app/interfaces/articles/PaginateInterface';
import ArticleInterface, {ParticipationData} from 'app/interfaces/articles/ArticleInterface';
import ParamsDataInterface from 'app/interfaces/articles/ParamsDataInterface';
import ArticleIndexInterface from 'app/interfaces/articles/ArticleIndexInterface';
import {urlName} from 'app/utils/articles/urlName';
import {updateOrAddObjectToStart} from 'app/utils/updateArraysObject';
import {getLanguageStorage} from 'app/utils/Language';
import {MainArticlesPayLoadInterface} from 'app/interfaces/stores/subcribe/MainArticlesPayLoadInterface';
import {ELEMENT_NOT_FOUND, REPEATABILITY_ONE_TIME} from 'app/utils/Consts';
import {pushAnalyticsEvent} from 'app/utils/analytics';
import PromoNewItemInterface from 'app/interfaces/articles/PromoNewItemInterface';
import PromoNewInterface from 'app/interfaces/articles/PromoNewInterface';
import {mergeGeneralPromoNewData, removePromoNewArticle, upsertPromoNewArticle} from 'app/utils/articles/promoNewArticles';

export interface ArticlePageInterface {
    data: ArticleInterface[]
    loading?: boolean
    paginate: PaginateInterface
    searchValue: string
}

const ARTICLE_TYPE_BETS_PROMO = 0;
const ARTICLE_TYPE_CASINO_PROMO = 1;
const ARTICLE_TYPE_PERMANENT_PROMO = 2;
const PROMO_WS_DEBOUNCE_MS = 100;

export class ArticlesStore {
    constructor() {
        makeObservable(this);
    }

    defaultArticleObject = {
        data: [],
        loading: false,
        paginate: {current_page: 1, per_page: 1, total_entries: 0, total_pages: 1},
        searchValue: ''
    };

    @observable blogData: ArticlePageInterface = this.defaultArticleObject;

    @observable newsData: ArticlePageInterface = this.defaultArticleObject;

    @observable promotionOldData: ArticlePageInterface = this.defaultArticleObject;

    @observable promotionArchiveData: ArticlePageInterface = this.defaultArticleObject;

    @observable hiddenTempleData: ArticlePageInterface = this.defaultArticleObject;

    @observable careersData: ArticlePageInterface = this.defaultArticleObject;

    @observable esportsData: ArticlePageInterface = this.defaultArticleObject;

    @observable sportData: ArticlePageInterface = this.defaultArticleObject;

    @observable casinoData: ArticlePageInterface = this.defaultArticleObject;

    @observable showEgbData: ArticlePageInterface = this.defaultArticleObject;

    @observable cupEgbData: ArticlePageInterface = this.defaultArticleObject;

    @observable siteNewsData: ArticlePageInterface = this.defaultArticleObject;

    @observable lastData: ArticlePageInterface = this.defaultArticleObject;

    @observable promoNewData: PromoNewInterface = {
        bets_promo: [],
        casino_promo: [],
        loading: false,
        permanent_promo: [],
        user_promo_view: []
    };

    @observable promoNewIndexItems: PromoNewItemInterface[] = [];

    @observable promoNewIndexLoading = false;

    @observable activePromoNewCount: number = 0;

    @observable homePageArticles: ArticleInterface[] = [];

    @observable sidebarArticles: ArticleInterface[] = [];

    @observable casinoPromoArticles: ArticleInterface[] = [];

    @observable loadingMainArticles = true;

    @observable pinnedArticles: ArticleInterface[] = [];

    @computed
    get promoActionsBonuses(): ArticleInterface[] {
        const {bets_promo, casino_promo, permanent_promo} = this.promoNewData;

        return [
            ...bets_promo,
            ...casino_promo,
            ...permanent_promo
        ]
            .filter(promo => promo.bonus_type && !(promo.participation?.completed && promo.repeatability === REPEATABILITY_ONE_TIME))
            .sort((a, b) => (a.ended_at ? 0 : 1) - (b.ended_at ? 0 : 1) || a.ended_at - b.ended_at);
    }

    @computed
    get participatedPromoCount(): number {
        const {bets_promo, casino_promo, permanent_promo} = this.promoNewData;

        return [
            ...bets_promo,
            ...casino_promo,
            ...permanent_promo
        ].filter(promo => promo.participation?.participating === true).length;
    }

    fields = {
        blog: 'blogData',
        careers: 'careersData',
        casino: 'casinoData',
        cupEgb: 'cupEgbData',
        esports: 'esportsData',
        hidden_temple: 'hiddenTempleData',
        hidden_temples: 'hiddenTempleData',
        last: 'lastData',
        news: 'newsData',
        promotions_archive: 'promotionArchiveData',
        promotions_old: 'promotionOldData',
        showEgb: 'showEgbData',
        siteNews: 'siteNewsData',
        sport: 'sportData'
    };

    subscription = null;

    private promoNewLoaded = false;

    private promoNewLoading = false;

    @action
    init = (params: ParamsDataInterface, type: string, search = ''): void => {
        this.loadData(params, search, type);
    };

    @action
    initPromoNew = (search = ''): void => {
        if (this.promoNewLoaded || this.promoNewLoading) {
            return;
        }

        this.loadPromoNewData(search);
    };

    @action
    refreshPromoNew = (search = ''): void => {
        if (this.promoNewLoading) {
            return;
        }

        this.loadPromoNewData(search);
    };

    @action
    loadPromoPageIndex = (): void => {
        if (this.promoNewIndexLoading) {
            return;
        }
        this.setPromoNewIndexData([]);
        this.setPromoNewIndexLoading(true);
        new Get({params: {}, url: getLinkPath('/promotions/new_index')}).execute()
            .then(response => response.json())
            .then(response => this.setPromoNewIndexData(response.data as PromoNewItemInterface[] || []))
            .finally(() => this.setPromoNewIndexLoading(false));
    };

    @action
    update = (data: ArticlePageInterface, type: string): void => this.setData(data, type);

    @action
    setData = (data: ArticlePageInterface, type: string): void => {
        this[this.fields[type]] = data;
    };

    @action
    setPromoNewIndexData = (items: PromoNewItemInterface[]): void => {
        this.promoNewIndexItems = items;
        this.activePromoNewCount = items.length;
    };

    @action
    setPromoNewIndexLoading = (loading: boolean): void => {
        this.promoNewIndexLoading = loading;
    };

    @action
    setPromoNewData = (data: PromoNewInterface): void => {
        const {bets_promo, casino_promo, permanent_promo} = data;

        this.mergeParticipationData(bets_promo);
        this.mergeParticipationData(casino_promo);
        this.mergeParticipationData(permanent_promo);

        this.activePromoNewCount = bets_promo.length + casino_promo.length + permanent_promo.length;
        this.promoNewData = data;
        this.setCasinoPromoArticles(data.casino_promo);
    };

    private promoWsDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    private pendingPromoNewUpdate: ((data: PromoNewInterface) => PromoNewInterface) | null = null;

    debouncedSetPromoNewData = (data: PromoNewInterface): void => {
        this.debouncePromoNewUpdate(() => data);
    };

    // Сливаем при применении, а не при получении: точечные события за время ожидания не затираются.
    debouncedMergeGeneralPromoNewData = (data: PromoNewInterface): void => {
        this.debouncePromoNewUpdate(current => mergeGeneralPromoNewData(current, data));
    };

    private debouncePromoNewUpdate = (update: (data: PromoNewInterface) => PromoNewInterface): void => {
        const pending = this.pendingPromoNewUpdate;
        this.pendingPromoNewUpdate = pending ? current => update(pending(current)) : update;

        if (this.promoWsDebounceTimer) {
            clearTimeout(this.promoWsDebounceTimer);
        }
        this.promoWsDebounceTimer = setTimeout(
            action('applyDebouncedPromoNewData', () => {
                const apply = this.pendingPromoNewUpdate;
                this.promoWsDebounceTimer = null;
                this.pendingPromoNewUpdate = null;
                this.setPromoNewData(apply(this.promoNewData));
            }),
            PROMO_WS_DEBOUNCE_MS
        );
    };

    // До загрузки ленты точечные события не применяем: свежие данные придут вместе с ней.
    @action
    upsertPromoNewArticle = (article: ArticleInterface): void => {
        if (this.promoNewLoaded) {
            this.setPromoNewData(upsertPromoNewArticle(this.promoNewData, article));
        }
    };

    @action
    removePromoNewArticle = ({id}: {id: number}): void => {
        if (this.promoNewLoaded) {
            this.setPromoNewData(removePromoNewArticle(this.promoNewData, id));
        }
    };

    mergeParticipationData(newPromos: ArticleInterface[]): void {
        const allExisting = [
            ...this.promoNewData.bets_promo,
            ...this.promoNewData.casino_promo,
            ...this.promoNewData.permanent_promo
        ];
        const participationMap = new Map(
            allExisting.filter(p => p.participation).map(p => [p.id, p.participation])
        );

        newPromos.forEach(promo => {
            if (!promo.participation && participationMap.has(promo.id)) {
                promo.participation = participationMap.get(promo.id);
            }
        });
    }

    @action
    setSearchValue = (value: string, type: string): void => {
        this[this.fields[type]].searchValue = value;
    };

    @action
    setLoadingStatus = (status: boolean, type: string): void => {
        this[this.fields[type]].loading = status;
    };

    @action
    setPromoNewLoadingStatus = (status: boolean): void => {
        this.promoNewData.loading = status;
    };

    setToUserLocale = (article: ArticleInterface): void => {
        const language = getLanguageStorage() === 'ru' ? 'ru' : 'en';
        article.title = article.title_locales[language];
        article.pre_content = article.pre_content_locales[language];
    };

    @action
    setHomePageArticles = (articles: ArticleInterface[]): void => {
        articles.forEach(article => this.setToUserLocale(article));
        this.homePageArticles = articles;
    };

    @action
    setSidebarArticles = (articles: ArticleInterface[]): void => {
        articles.forEach(article => this.setToUserLocale(article));
        this.sidebarArticles = articles;
    };

    @action
    setPinnedArticles = (articles: ArticleInterface[]): void => {
        articles.forEach(article => this.setToUserLocale(article));
        this.pinnedArticles = articles;
    };

    @action
    setCasinoPromoArticles = (articles: ArticleInterface[]): void => {
        const casinoArticles: ArticleInterface[] = articles?.filter(
            (article: ArticleInterface) => article?.single_locale === getLanguageStorage() || !article?.single_locale
        ) || [];
        casinoArticles.forEach((article: ArticleInterface) => this.setToUserLocale(article));
        this.casinoPromoArticles = casinoArticles;
    };

    @action
    addOrReplace = (arr: ArticleInterface[], newObj: ArticleInterface): ArticleInterface[] => [...arr.filter(o => o.id !== newObj.id), {...newObj}];

    @action
    updateElementCollection(item: ArticleInterface, data: ArticlePageInterface): void {
        this.setToUserLocale(item);
        if (item.article_type === 'promotion' || item.article_type === 'news' || item.article_type === 'casino') {
            if (item.deleted) {
                this.homePageArticles = this.homePageArticles.filter(a => a.id !== item.id);
            } else if (item.main_image) {
                this.homePageArticles = this.addOrReplace(this.homePageArticles, item)
                    .sort((a, b) => a.priority > b.priority ? 1 : ELEMENT_NOT_FOUND);
            }
        }
        switch (item.promo_type) {
        case ARTICLE_TYPE_BETS_PROMO:
            updateOrAddObjectToStart(this.promoNewData, 'bets_promo', item);
            break;
        case ARTICLE_TYPE_CASINO_PROMO:
            updateOrAddObjectToStart(this.promoNewData, 'casino_promo', item);
            break;
        case ARTICLE_TYPE_PERMANENT_PROMO:
            updateOrAddObjectToStart(this.promoNewData, 'permanent_promo', item);
            break;
        default:
            if (data) {
                updateOrAddObjectToStart(data, 'data', item);
            }
            break;
        }
        this.setCasinoPromoArticles(this.promoNewData.casino_promo);
    }

    @action
    hideExpiredPromoNewData = (id: number, type: string): void => {
        const newPromoNewData = this.promoNewData[`${type}_promo`].filter(p => p.id !== id);
        this.setPromoNewData({...this.promoNewData, [`${type}_promo`]: newPromoNewData});
    };

    @action
    hideExpiredPromoNewIndexItem = (id: number): void => {
        this.promoNewIndexItems = this.promoNewIndexItems.filter(p => p.id !== id);
        this.activePromoNewCount = this.promoNewIndexItems.length;
    };

    @action
    updatePromoNewIndexItem = (item: PromoNewItemInterface): void => {
        if (!item?.id) {
            return;
        }

        this.promoNewIndexItems = this.promoNewIndexItems.map(promo => promo.id === item.id ? item : promo);
    };

    getDataByType = (type: string): ArticlePageInterface => this[this.fields[type]];

    getArticlesToAside(type: string): ArticleInterface[] {
        if (type === 'news') {
            return newsArticles(this.sidebarArticles);
        } else if (type === 'promotions') {
            return promotionArticles(this.sidebarArticles);
        }
        return [];
    }

    loadArticleByTypeAndId = (type: string, id: string, setData: (data: ArticleIndexInterface) => void): void => {
        new Get({params: {json: true}, url: getLinkPath(`/${type}/${id}`)})
            .execute()
            .then(response => response.json())
            .then(response => setData(response));
    };

    createUserNewsView = (promo: ArticleInterface): void => {
        new Post({params: {}, url: getLinkPath(`/promotions/${promo.slug}/create_user_news_view`)})
            .execute()
            .then(() => this.markPromoAsViewed(promo.id));
    };

    participateInPromo = (promoId: number): Promise<void> => new Post({params: {}, url: getLinkPath(`/promotions/${promoId}/participate`)})
        .execute()
        .then(response => response.json())
        .then(response => {
            if (response.success && response.participation) {
                pushAnalyticsEvent('bonus_claim', {
                    promotion_id: promoId,
                    source: 'promotion_participation'
                });
                this.updatePromoParticipation(
                    promoId,
                    response.participation,
                    response.participants_count
                );
            }
        });

    @action
    updatePromoParticipation = (
        promoId: number,
        participation: ParticipationData,
        participantsCount?: number
    ): void => {
        this.promoNewIndexItems = this.promoNewIndexItems.map(promo => {
            if (promo.id !== promoId || !promo.promo_bonus) {
                return promo;
            }
            return {
                ...promo,
                participants_count: participantsCount ?? promo.participants_count,
                promo_bonus: {...promo.promo_bonus, participation}
            };
        });
        this.promoNewData = {
            ...this.promoNewData,
            bets_promo: this.promoNewData.bets_promo.map(p => p.id === promoId ? {...p, participation} : p),
            casino_promo: this.promoNewData.casino_promo.map(p => p.id === promoId ? {...p, participation} : p),
            permanent_promo: this.promoNewData.permanent_promo.map(p => p.id === promoId ? {...p, participation} : p)
        };
    };

    @action
    markPromoAsViewed = (id: number): void => {
        const promo = this.promoNewData.user_promo_view.find(p => p.id === id);

        if (promo) {
            promo.viewed = true;
        }
    };

    hasUnviewedPromo = (): boolean => this.promoNewData.user_promo_view.some(item => item.viewed === false);

    @action
    setLoadingMainArticles = (loading: boolean): void => {
        this.loadingMainArticles = loading;
    };

    loadMainArticles = (): void => {
        new Get({params: {json: true}, url: getLinkPath('/get_main_articles')})
            .execute()
            .then(response => response.json())
            .then(response => {
                this.setHomePageArticles(response.home_page);
                this.setSidebarArticles(response.sidebar);
            }).finally(() => {
                this.setLoadingMainArticles(false);
            });
    };

    loadPinnedArticles = (): void => {
        new Get({params: {json: true}, url: getLinkPath('/pinned_articles')})
            .execute()
            .then(response => response.json())
            .then(response => {
                this.setPinnedArticles(response.articles);
            }).finally(() => {
                this.setLoadingMainArticles(false);
            });
    };

    loadData = (params: ParamsDataInterface, search: string, type: string): void => {
        this.setLoadingStatus(true, type);
        new Get({params: {...params, search}, url: urlName(type)})
            .execute()
            .then(response => response.json())
            .then(response => {
                if (response.by_categories) {
                    Object.keys(response.data).forEach(category => {
                        this.setData({
                            data: response.data[category],
                            loading: false,
                            paginate: response.paginate,
                            searchValue: search
                        }, category);
                    });
                    this.setLoadingStatus(false, type);
                    return;
                }
                this.setData({
                    data: response.data,
                    loading: false,
                    paginate: response.paginate,
                    searchValue: search
                }, type);
            });
    };

    loadPromoNewData = (search: string): void => {
        this.promoNewLoading = true;
        this.setPromoNewLoadingStatus(true);
        new Get({params: {search}, url: getLinkPath('/promotions')})
            .execute()
            .then(response => response.json())
            .then(response => {
                this.setPromoNewData(response);
                this.promoNewLoaded = true;
            }).finally(() => {
                this.promoNewLoading = false;
                this.setPromoNewLoadingStatus(false);
            });
    };

    setEmptyPromoNewData = (): void => {
        this.setPromoNewData({bets_promo: [], casino_promo: [], loading: false, permanent_promo: [], user_promo_view: []});
    };

    listenMain = (payload: MainArticlesPayLoadInterface): void => {
        if (payload.update_article &&
            (!payload.update_article.single_locale ||
                payload.update_article.single_locale === getLanguageStorage())) {
            this.updateElementCollection(payload.update_article,
                this[this.fields[payload.update_article.article_type]]);
            if (payload.pinnedArticles) {
                this.setPinnedArticles(payload.pinnedArticles);
            }
        }
    };
}
